import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/**
 * ===========================================================================
 * COMMUNICATIONS
 * ===========================================================================
 * Two things, because only two have somewhere to arrive: a message to a
 * driver, and an email to drivers or customers.
 *
 * WHAT WENT, AND WHY. The old screen configured four automatic notifications —
 * break ending, clock-out due, clock-in missing, pharmacist not arrived. Every
 * one fires off a SCHEDULED time, and the driver app records a clock-in, a
 * clock-out and two odometer readings. There is no schedule to be late
 * against. Those switches could never have fired, and a switch that cannot
 * fire is worse than no switch, because somebody turns it on and stops
 * watching.
 *
 * PUSH IS LIVE, AND IT IS THE ONLY DOORBELL. A message is saved and then
 * pushed to the driver's registered phone through Firebase. The app no longer
 * polls, so a push that does not arrive is a message the driver is not told
 * about until they next open their messages. Two things follow for this
 * screen: it must say what actually happened to each send (delivered, failed
 * and WHY, no phone registered), and it must show up front whether the push
 * chain works at all - credentials, a registered phone, and a validate-only
 * dry run against that phone - before anybody relies on it.
 */

const S = {
  wrap:  { padding: 16, fontFamily: "system-ui, sans-serif", color: "#111827" },
  h1:    { fontSize: 18, fontWeight: 700, margin: 0 },
  panel: { border: "1px solid #d1d5db", borderRadius: 10, background: "#fcfcfd",
           padding: "12px 14px" },
  label: { fontSize: 10.5, fontWeight: 800, color: "#6b7280",
           textTransform: "uppercase", letterSpacing: 0.5 },
  input: { padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db",
           borderRadius: 4, width: "100%", boxSizing: "border-box",
           fontFamily: "inherit" },
  btn:   { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db",
           borderRadius: 4, padding: "6px 10px", fontSize: 13, cursor: "pointer",
           fontFamily: "inherit" },
  btnGo: { background: "#0f766e", color: "#fff", border: "1px solid #0f766e",
           borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer",
           fontFamily: "inherit", fontWeight: 600 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th:    { textAlign: "left", padding: "5px 7px", borderBottom: "2px solid #e5e7eb",
           fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
           color: "#6b7280" },
  td:    { padding: "5px 7px", borderBottom: "1px solid #f3f4f6" },
  muted: { color: "#6b7280", fontSize: 11 },
  error: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
           padding: 10, borderRadius: 6, fontSize: 13 },
  ok:    { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534",
           padding: 10, borderRadius: 6, fontSize: 13 },
};

const TABS = [
  { key: "message", label: "Message drivers" },
  { key: "email",   label: "Email" },
  { key: "log",     label: "Sent email log" },
];

async function api(path, options = {}) {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE}/api/comms${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data && data.error && data.error.message) || `Failed (${res.status})`);
  }
  return data;
}

const when = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "—" : t.toLocaleString("en-GB");
};

export default function Communications() {
  const [tab, setTab] = useState("message");
  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", gap: 12, alignItems: "center",
                    marginBottom: 12, flexWrap: "wrap" }}>
        <h1 style={S.h1}>Communications</h1>
        <MaskToggleButton />
        <div style={{ display: "flex", gap: 6 }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ ...S.btn,
                background: tab === t.key ? "#0f766e" : "#f3f4f6",
                color: tab === t.key ? "#fff" : "#374151",
                borderColor: tab === t.key ? "#0f766e" : "#d1d5db" }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "message" && <MessageDrivers />}
      {tab === "email" && <EmailPanel />}
      {tab === "log" && <EmailLog />}
    </div>
  );
}

/** Shared multi-select list. Selection is by id, never by index. */
function PickList({ rows, selected, onToggle, render, empty }) {
  if (!rows.length) return <div style={S.muted}>{empty}</div>;
  return (
    <div style={{ maxHeight: "48vh", overflowY: "auto" }}>
      {rows.map((r) => (
        <label key={r.id}
          style={{ display: "flex", gap: 8, alignItems: "flex-start",
                   padding: "5px 6px", borderBottom: "1px solid #f3f4f6",
                   cursor: "pointer" }}>
          <input type="checkbox" checked={selected.has(r.id)}
                 onChange={() => onToggle(r.id)} style={{ marginTop: 3 }} />
          <span style={{ fontSize: 13 }}>{render(r)}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Is push going to work? Asked of the server on every visit and shown at the
 * top of the tab, because the answer decides whether a "Sent" here means a
 * phone buzzed or a row was written.
 *
 * The server checks three things that have each been the real fault: it can
 * mint a Firebase token (key valid), some driver has registered a phone, and
 * a validate-only dry run to that phone is accepted. The last is the only
 * check that catches the app being built against a DIFFERENT Firebase project
 * from the server's key - the credentials are fine, the phone is fine, and
 * every real send fails.
 */
function PushStatus({ status, error, onRetry }) {
  if (error) {
    return (
      <div style={{ ...S.error, marginBottom: 10 }}>
        Could not check push: {error}{" "}
        <button style={S.btn} onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (!status) return <div style={{ ...S.muted, marginBottom: 10 }}>Checking push…</div>;

  const box = (bg, border, color, children) => (
    <div style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 6,
                  padding: "8px 10px", fontSize: 12.5, marginBottom: 10 }}>
      {children}
    </div>
  );
  const devices = `${status.driversWithDevice} of ${status.drivers} driver${status.drivers === 1 ? "" : "s"} `
    + `${status.driversWithDevice === 1 ? "has" : "have"} the app registered`;

  switch (status.verdict) {
    case "live":
      return box("#f0fdf4", "#bbf7d0", "#166534", (
        <>
          <b>Push notifications are live</b> (Firebase project {status.projectId}).{" "}
          {devices}. Dry run to {status.probe.driverName}'s phone accepted.
        </>
      ));
    case "no_devices":
      return box("#fffbeb", "#fde68a", "#92400e", (
        <>
          <b>Push is set up but no phone has registered yet</b> (Firebase project{" "}
          {status.projectId}). {devices}. A driver registers by signing in to the
          app; until then messages are saved and seen when they open them.
        </>
      ));
    case "probe_failed":
      return box("#fef2f2", "#fecaca", "#991b1b", (
        <>
          <b>Push will not reach the phones.</b> The server can talk to Firebase
          (project {status.projectId}) but a dry run to {status.probe.driverName}'s
          phone was refused: {status.probe.error}
          {status.probe.code === "SENDER_ID_MISMATCH" && (
            <div style={{ marginTop: 4 }}>
              Fix: in the Firebase console for the project the APP is built
              against, Project settings → Service accounts → Generate new private
              key, and put its project_id, client_email and private_key into the
              server's FCM_PROJECT_ID, FCM_CLIENT_EMAIL and FCM_PRIVATE_KEY, then
              restart the API.
            </div>
          )}
        </>
      ));
    case "credentials_rejected":
      return box("#fef2f2", "#fecaca", "#991b1b", (
        <>
          <b>Firebase rejected the server's credentials</b>
          {status.projectId ? ` (project ${status.projectId})` : ""}: {status.credentialsError}.
          Messages are still saved; no phone will buzz until this is fixed.
        </>
      ));
    default:
      return box("#f3f4f6", "#d1d5db", "#374151", (
        <>
          <b>Push is not configured on the server.</b> {status.credentialsError}.
          Messages are saved and seen when each driver next opens them in the app.
        </>
      ));
  }
}

/**
 * What happened to a send, in one honest sentence. "Saved for 3" on its own
 * used to read as "3 phones buzzed"; it never meant that.
 */
function SendResult({ result }) {
  const noApp = Math.max(0, result.sent - result.pushable);
  const parts = [];
  if (!result.pushConfigured) {
    parts.push("Push is not configured on the server, so they will see it when they next open their messages in the app.");
  } else {
    if (result.pushDelivered > 0) {
      parts.push(`Pushed to ${result.pushDelivered} phone${result.pushDelivered === 1 ? "" : "s"}.`);
    }
    if (result.pushFailed > 0) {
      parts.push(`${result.pushFailed} push${result.pushFailed === 1 ? "" : "es"} failed.`);
    }
    if (result.tokensRemoved > 0) {
      parts.push(`${result.tokensRemoved} dead registration${result.tokensRemoved === 1 ? "" : "s"} removed - that driver needs to sign in to the app again to be reachable.`);
    }
    if (noApp > 0) {
      parts.push(`${noApp} ${noApp === 1 ? "has" : "have"} no app registered and will see it when they next open their messages.`);
    }
  }
  const bad = result.pushFailed > 0 || result.tokensRemoved > 0;
  return (
    <div style={{ ...(bad ? S.error : S.ok), marginBottom: 10 }}>
      Saved for {result.sent} driver{result.sent === 1 ? "" : "s"}. {parts.join(" ")}
      {result.pushErrors && result.pushErrors.length > 0 && (
        <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
          {result.pushErrors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

function MessageDrivers() {
  const masked = useMaskNames();
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [content, setContent] = useState("");
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushError, setPushError] = useState(null);

  const load = useCallback(async () => {
    try { setDrivers(await api("/drivers")); }
    catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Separate from the driver list: a slow or failing Firebase check must not
  // hold up choosing who to message, and its failure is its own message.
  const checkPush = useCallback(async () => {
    setPushStatus(null); setPushError(null);
    try { setPushStatus(await api("/push_status")); }
    catch (e) { setPushError(e.message); }
  }, []);
  useEffect(() => { checkPush(); }, [checkPush]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const unreachable = useMemo(
    () => drivers.filter((d) => selected.has(d.userId) && !d.reachable).length,
    [drivers, selected]);

  const send = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api("/messages", {
        method: "POST",
        body: JSON.stringify({ driverIds: [...selected], content }),
      });
      setResult(r);
      setContent("");
      setSelected(new Set());
      load();
      // A send is the most informative probe there is: if a registration
      // just died, the status line should say so now, not on the next visit.
      if (r.tokensRemoved > 0 || r.pushFailed > 0) checkPush();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ ...S.panel, flex: "0 0 330px" }}>
        <div style={{ ...S.label, marginBottom: 6 }}>Drivers</div>
        <PickList
          rows={drivers.map((d) => ({ ...d, id: d.userId }))}
          selected={selected}
          onToggle={toggle}
          empty="No drivers."
          render={(d) => (
            <>
              {displayName(d.fullName, masked)}
              <div style={S.muted}>
                {d.branchName}
                {/* Said on the row, not after sending. Whether a phone can be
                    woken is worth knowing while choosing who to tell. */}
                {!d.reachable && " · app not registered"}
                {d.unread > 0 && ` · ${d.unread} unread`}
              </div>
            </>
          )}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex",
                    flexDirection: "column", gap: 12 }}>
        <div style={S.panel}>
          <PushStatus status={pushStatus} error={pushError} onRetry={checkPush} />
          {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
          {result && <SendResult result={result} />}

          <div style={S.label}>Message</div>
          <textarea rows={4} style={{ ...S.input, marginTop: 4 }}
            value={content} maxLength={2000}
            placeholder="Depot closes at 4 today — last round out by 2."
            onChange={(e) => setContent(e.target.value)} />

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <button style={S.btnGo} disabled={busy || !content.trim() || !selected.size}
                    onClick={send}>
              {busy ? "Sending…" : `Send to ${selected.size || "…"}`}
            </button>
            <span style={S.muted}>{content.length}/2000</span>
            {unreachable > 0 && (
              <span style={{ fontSize: 12, color: "#b45309" }}>
                {unreachable} of those {unreachable === 1 ? "has" : "have"} no app
                registered - they will see it when they next open their messages.
              </span>
            )}
          </div>
        </div>

        <div style={S.panel}>
          <div style={{ ...S.label, marginBottom: 6 }}>Conversation</div>
          <select style={{ ...S.input, maxWidth: 300 }}
            onChange={async (e) => {
              const id = e.target.value;
              if (!id) { setThread(null); return; }
              try { setThread(await api(`/threads/${id}`)); }
              catch (err) { setError(err.message); }
            }}>
            <option value="">Pick a driver…</option>
            {drivers.map((d) => (
              <option key={d.userId} value={d.userId}>
                {displayName(d.fullName, masked)}
              </option>
            ))}
          </select>

          {thread && (thread.messages.length === 0 ? (
            <div style={{ ...S.muted, marginTop: 8 }}>Nothing sent yet.</div>
          ) : (
            <div style={{ marginTop: 8, maxHeight: "34vh", overflowY: "auto" }}>
              {thread.messages.map((m) => (
                <div key={m.id} style={{
                  padding: "6px 8px", borderRadius: 6, marginBottom: 5,
                  background: m.direction === "to_driver" ? "#f0fdfa" : "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}>
                  <div style={{ fontSize: 13 }}>{m.content}</div>
                  <div style={S.muted}>
                    {m.direction === "to_driver" ? "Sent" : "From driver"} ·{" "}
                    {when(m.createdAt)}
                    {m.direction === "to_driver" && (
                      m.readAt ? ` · read ${when(m.readAt)}` : " · unread")}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailPanel() {
  const masked = useMaskNames();
  const [audience, setAudience] = useState("drivers");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setSelected(new Set());
    (async () => {
      try {
        if (audience === "drivers") {
          const d = await api("/drivers");
          setRows(d.filter((x) => x.email)
            .map((x) => ({ id: x.userId, name: x.fullName, sub: x.email })));
        } else {
          const q = search ? `?search=${encodeURIComponent(search)}` : "";
          const c = await api(`/customers${q}`);
          setRows(c.map((x) => ({ id: x.id, name: x.fullName, sub: x.email })));
        }
      } catch (e) { setError(e.message); }
    })();
  }, [audience, search]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const send = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api("/email", {
        method: "POST",
        body: JSON.stringify({ audience, ids: [...selected], subject, body }),
      });
      setResult(r);
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ ...S.panel, flex: "0 0 330px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[["drivers", "Drivers"], ["customers", "Customers"]].map(([v, l]) => (
            <button key={v} onClick={() => setAudience(v)}
              style={{ ...S.btn, flex: 1,
                background: audience === v ? "#0f766e" : "#f3f4f6",
                color: audience === v ? "#fff" : "#374151",
                borderColor: audience === v ? "#0f766e" : "#d1d5db" }}>
              {l}
            </button>
          ))}
        </div>

        {audience === "customers" && (
          <input style={{ ...S.input, marginBottom: 8 }} value={search}
            placeholder="Name, email or postcode"
            onChange={(e) => setSearch(e.target.value)} />
        )}

        {/* Only people with an address are listed at all. Showing someone you
            cannot email and failing after the fact wastes the sender's time. */}
        <PickList
          rows={rows} selected={selected} onToggle={toggle}
          empty={audience === "drivers"
            ? "No drivers have an email address on file."
            : "No customers with an email address match."}
          render={(r) => (
            <>
              {displayName(r.name, masked)}
              <div style={S.muted}>{r.sub}</div>
            </>
          )}
        />
      </div>

      <div style={{ ...S.panel, flex: 1, minWidth: 0 }}>
        {error && <div style={{ ...S.error, marginBottom: 10 }}>{error}</div>}
        {result && (
          <div style={{ ...S.ok, marginBottom: 10 }}>
            {result.sent} sent
            {result.queued > 0 && `, ${result.queued} queued (no mail server configured)`}
            {result.attempted < result.requested && (
              <> · {result.requested - result.attempted} skipped with no address on file</>
            )}
          </div>
        )}

        <div style={S.label}>Subject</div>
        <input style={{ ...S.input, marginTop: 4, marginBottom: 10 }}
          value={subject} maxLength={255}
          onChange={(e) => setSubject(e.target.value)} />

        <div style={S.label}>Message</div>
        <textarea rows={10} style={{ ...S.input, marginTop: 4 }}
          value={body} onChange={(e) => setBody(e.target.value)} />

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <button style={S.btnGo}
            disabled={busy || !subject.trim() || !body.trim() || !selected.size}
            onClick={send}>
            {busy ? "Sending…" : `Send to ${selected.size || "…"}`}
          </button>
          {/* Sent individually, so nobody sees anybody else's address. Worth
              stating, because the alternative is what people assume. */}
          <span style={S.muted}>
            Sent one at a time — recipients never see each other.
          </span>
        </div>
      </div>
    </div>
  );
}

function EmailLog() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    api("/emails").then(setRows).catch((e) => setError(e.message));
  }, []);

  if (error) return <div style={S.error}>{error}</div>;
  if (!rows.length) return <div style={{ ...S.panel, ...S.muted }}>Nothing sent yet.</div>;

  const TONE = { sent: "#dcfce7", failed: "#fee2e2", queued: "#fef3c7" };

  return (
    <div style={S.panel}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>To</th>
            <th style={S.th}>Subject</th>
            <th style={S.th}>Kind</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={S.td}>{r.to}</td>
              <td style={S.td}>{r.subject}</td>
              <td style={{ ...S.td, ...S.muted }}>{r.kind}</td>
              <td style={{ ...S.td, background: TONE[r.status] }}>
                {r.status}
                {r.error && <div style={S.muted}>{r.error}</div>}
              </td>
              <td style={S.td}>{when(r.sentAt || r.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
